/**
 * A plain sector commander for the factions nobody is playing: every idle
 * squad goes for the nearest system it does not hold, enemy home first when
 * it is adjacent. Deterministic; just enough to make a sheet move.
 */

import { aliveUnits, buyAmmo, issueOrder, neighbours, pathBetween, pathDays, restsHere, runningModelNow, squadsAt, systemById } from './sector';
import { knownView, discountedStrength } from './knowledge';
import { sneakDecision } from './doctrine';
import { garrisonWatches } from './garrison';
import { systemWorth } from './worth';
import { traceHops } from './signatures';
import { stanceInWindow } from './weather';
import { waitForPass } from './satellites';
import type { FactionId, SectorState, Stance, SystemId } from './types';
import type { SectorOrders } from './turn';

/** A report older than this is treated as unknown: the garrison scouts it rather than trusting it. */
export const KNOWLEDGE_STALE_DAYS = 4;

export interface CommanderOptions {
  /** Plan on the truth instead of the faction's knowledge (the harness's control row). Default false. */
  omniscient?: boolean;
  /** How to approach a garrisoned enemy system: `auto` sneaks when outnumbered by what it knows is there. Default auto. */
  stance?: Stance | 'auto';
  /** Build on held garrisons when supply allows (a depot, then an antenna, then ecotourism). Default true. */
  prepare?: boolean;
  /** 56.8: also build a sensor (watches two lanes out) after the antenna. Default false. */
  sensors?: boolean;
  /** 56.8: the garrison scouts the neighbour a look would tell the most about (stale report × an enemy plausibly there), not just the oldest. Default false. */
  scoutByValue?: boolean;
  /**
   * 62.17 step 5: read the battles under way (`state.running`, the model) — send an idle squad within
   * `REINFORCE_DAYS` to one where our side is not winning (it joins on arrival), and pull our squads
   * out of one we are losing badly (not at our home). Default true; no effect without running battles.
   */
  runningBattles?: boolean;
}

/** X26: hops of path a point of worth is good for when the stand-in values systems (a 3-planet intact system ≈ one hop). */
export const VALUE_HOPS = 0.3;

/** A reinforcing squad must reach the battle within this many days. */
export const REINFORCE_DAYS = 2;
/** Our engaged health below this share of theirs is losing badly: withdraw (unless the battle is for our home). */
export const WITHDRAW_BELOW = 0.5;
/** Our health below this share of theirs is "not winning": a reinforcement is worth sending. */
export const REINFORCE_BELOW = 1.2;

/** What a side's troops report of their battle under way: own and enemy living health, from the model as it stands. */
export function battleOutlook(truth: SectorState, r: NonNullable<SectorState['running']>[number], faction: FactionId): { own: number; enemy: number; side: 0 | 1 } | null {
  const side = r.setup.sides.findIndex(s => s.faction === faction);
  if (side < 0) return null;
  const now = runningModelNow(truth, r);
  if (!now || now.ended) return null;
  const health = (i: number) => now.sides[i].units.reduce((a, u) => a + (u.alive ? u.health : 0), 0);
  return { own: health(side), enemy: health(1 - side), side: side as 0 | 1 };
}

/**
 * Commander styles — the same squads under different orders, which is what the
 * harness's map-matters test compares (RFC Q12). `balanced` keeps a garrison and
 * sends the rest; `allIn` sends everyone at the nearest enemy-held system;
 * `turtle` brings everyone home and only strikes an adjacent enemy home.
 */
export type CommanderStyle = 'balanced' | 'allIn' | 'turtle' | 'hold';
export const COMMANDER_STYLES: CommanderStyle[] = ['balanced', 'allIn', 'turtle'];

/**
 * The orders this commander would give its idle squads right now (pure; nothing
 * issued). Plans on `knownView(state, faction)` — what the faction has seen —
 * unless `omniscient`; the garrison scouts an adjacent system whose report is
 * stale or missing before anyone marches on it.
 */
export function planFactionTurn(truth: SectorState, faction: FactionId, style: CommanderStyle = 'balanced', opts: CommanderOptions = {}): SectorOrders {
  const orders: SectorOrders = {};
  const state = opts.omniscient ? truth : knownView(truth, faction);
  const me = state.factions.find(f => f.id === faction);
  if (!me) return orders;
  // Scouting: a squad holding at home looks at the adjacent system with the oldest (or no) report (allIn sends everyone instead)
  if (!opts.omniscient && style !== 'allIn') {
    const k = truth.knowledge[faction];
    const home = state.squads.find(q => q.faction === faction && q.at === me.home && q.order.kind === 'hold' && aliveUnits(q).length > 0);
    if (home && k) {
      const stale = neighbours(state, me.home)
        .map(n => ({ n, age: k.systems[n] ? truth.day - k.systems[n].seenDay : Infinity }))
        .filter(x => x.age >= KNOWLEDGE_STALE_DAYS)
        .map(x => ({ ...x, value: opts.scoutByValue ? scoutValue(state, faction, x.n, x.age) : x.age }))
        .sort((a, b) => b.value - a.value)[0];
      if (stale) orders[home.id] = { kind: 'scout', target: stale.n, since: truth.day };
    }
  }
  // 62.17 step 5: battles under way where we fight — withdraw from a rout, reinforce a close one
  if (opts.runningBattles !== false) {
    for (const r of truth.running ?? []) {
      const look = battleOutlook(truth, r, faction);
      if (!look) continue;
      const sysId = r.setup.systemId;
      if (look.own < look.enemy * WITHDRAW_BELOW && sysId !== me.home) {
        for (const id of r.setup.sides[look.side].squadIds) {
          const q = truth.squads.find(x => x.id === id);
          if (q && q.at === sysId && aliveUnits(q).length) orders[id] = { kind: 'move', to: me.home };
        }
        continue;
      }
      if (look.own < look.enemy * REINFORCE_BELOW) {
        const fighting = new Set(r.setup.sides[look.side].squadIds);
        const candidates = state.squads
          .filter(q => q.faction === faction && q.at !== null && q.at !== sysId && q.order.kind === 'hold' && aliveUnits(q).length > 0 && !fighting.has(q.id) && !orders[q.id])
          .map(q => ({ q, days: pathDays(state, q.at!, sysId) }))
          .filter((x): x is { q: typeof x.q; days: number } => x.days !== null && x.days <= REINFORCE_DAYS)
          .sort((a, b) => a.days - b.days || (a.q.id < b.q.id ? -1 : 1));
        if (candidates.length) orders[candidates[0].q.id] = { kind: 'move', to: sysId };
      }
    }
  }
  const enemyHomes = state.factions.filter(f => f.id !== faction).map(f => f.home);
  // What the enemy at a system counts for: its last-seen strength, discounted by the report's age (56.8; off by default)
  const strengthAt = (systemId: SystemId): number => squadsAt(state, systemId).filter(x => x.faction !== faction)
    .reduce((a, x) => a + (opts.omniscient ? aliveUnits(x).length : discountedStrength(truth, faction, x.id, aliveUnits(x).length)), 0);
  // One squad stays home as the garrison (losing the home loses the sector) — unless it is the only squad
  const mine = state.squads.filter(q => q.faction === faction && aliveUnits(q).length > 0);
  const garrison = style !== 'allIn' && mine.length > 1 ? mine.find(q => q.at === me.home) : undefined;
  for (const q of state.squads) {
    if (q.faction !== faction || q.at === null || q.order.kind !== 'hold' || !aliveUnits(q).length) continue;
    if (orders[q.id]) continue;
    if (garrison && q.id === garrison.id) continue;
    // a squad fighting in a battle under way stays: an order out would withdraw its whole side (62.17 step 4); only the
    // running-battle logic above withdraws, and only from a rout (found by the first scenario, Oct 10)
    if ((truth.running ?? []).some(r => r.setup.systemId === q.at && r.setup.sides.some(sd => sd.faction === faction && sd.squadIds.includes(q.id)))) continue;
    // Wounded squads go home to hold (and heal, when the rule is on)
    const avg = aliveUnits(q).reduce((a, u) => a + u.health, 0) / aliveUnits(q).length;
    if (avg < 35 && q.at !== me.home) { orders[q.id] = { kind: 'move', to: me.home }; continue; }
    // Standing on a system we do not hold with nobody to fight (what stands beside us is always known): secure it
    const here = systemById(state, q.at);
    const contested = squadsAt(truth, q.at).some(x => x.faction !== faction && aliveUnits(x).length > 0);
    if (style !== 'turtle' && here.owner !== faction && !contested) { orders[q.id] = { kind: 'secure', since: truth.day }; continue; }
    // Holding a garrisoned system with supply to spare: build (a depot where there is none, then an antenna, then ecotourism)
    if (opts.prepare !== false && here.owner === faction && here.garrisoned && here.structures.length < truth.rules.maxStructuresPerSystem) {
      const supply = truth.factions.find(f => f.id === faction)?.supply ?? 0;
      const reserve = truth.rules.reinforceDays > 0 ? truth.rules.squadCost : 0;
      if (supply - truth.rules.prepareCost >= reserve) {
        const kinds = here.structures.map(b => b.kind);
        const watching = truth.rules.adaptiveGarrisons && garrisonWatches(systemById(truth, here.id));   // X17: answer the sneak with eyes
        const build = watching && !kinds.includes('sensor') ? 'sensor' : here.id !== me.home && !kinds.includes('depot') ? 'depot' : !kinds.includes('antenna') ? 'antenna' : opts.sensors && !kinds.includes('sensor') ? 'sensor' : !kinds.includes('ecotourism') ? 'ecotourism' : null;
        if (build) { orders[q.id] = { kind: 'prepare', build, since: truth.day }; continue; }
      }
    }
    // hold (62.42 #1, the first scenario's defensive commander): keep what you hold, strike only next door
    if (style === 'hold') {
      const target = holdTarget(state, faction, q, strengthAt);
      if (target) orders[q.id] = { kind: 'move', to: target, stance: stanceFor(state, faction, q, target, opts.stance ?? 'auto', strengthAt) };
      continue;
    }
    if (style === 'turtle') {
      const strike = neighbours(state, q.at).find(n => enemyHomes.includes(n) && systemById(state, n).owner !== faction);
      if (q.at === me.home && strike && mine.length > 1) { orders[q.id] = { kind: 'move', to: strike }; continue; }
      if (q.at !== me.home) orders[q.id] = { kind: 'move', to: me.home };
      continue;
    }
    const target = style === 'allIn' ? pickEnemyHeld(state, faction, q.at) : pickTarget(state, faction, q.at, enemyHomes, strengthAt);
    if (target && target !== q.at) {
      const stance = stanceFor(state, faction, q, target, opts.stance ?? 'auto', strengthAt);
      // X4 (rules.satellites): a sneak that would land under an enemy pass waits a turn — the orbit moves on
      const days = pathDays(state, q.at, target);
      if (stance === 'sneak' && days !== null && waitForPass(truth, faction, target, truth.day + days * truth.rules.sneakTravelMult)) continue;
      orders[q.id] = { kind: 'move', to: target, stance };
    }
  }
  return orders;
}

/**
 * Sneak at a garrisoned enemy system when what we know is there outweighs us by the squad's doctrine's odds
 * (62.14: `doctrine.ts sneakDecision` — caution 0.5 = outnumbered, today's rule); assault otherwise.
 */
function stanceFor(state: SectorState, faction: FactionId, q: SectorState['squads'][number], target: SystemId, pref: Stance | 'auto', strengthAt: (id: SystemId) => number): Stance {
  if (pref !== 'auto') return pref;
  const sys = systemById(state, target);
  if (!sys.owner || sys.owner === faction || !sys.garrisoned) return 'assault';
  const stance = sneakDecision(q.doctrine, aliveUnits(q).length, strengthAt(target));
  // X6 (rules.weatherWindows): a window that hides is the moment to sneak
  return state.rules.weatherWindows ? stanceInWindow(state, faction, target, stance) : stance;
}

/**
 * 56.8: what a look at a neighbour would tell us — its report's age (never seen = most), times how plausibly
 * an enemy is there: held by an enemy or unknown, or an enemy squad last seen there or one lane from it.
 */
function scoutValue(state: SectorState, faction: FactionId, systemId: SystemId, age: number): number {
  const sys = systemById(state, systemId);
  const ageFactor = isFinite(age) ? Math.min(1, age / (KNOWLEDGE_STALE_DAYS * 2)) : 1;
  let plausible = sys.owner === null ? 0.5 : sys.owner === faction ? 0.25 : 1;
  const near = [systemId, ...neighbours(state, systemId)];
  if (state.squads.some(q => q.faction !== faction && q.at !== null && near.includes(q.at) && aliveUnits(q).length > 0)) plausible = 1;
  return ageFactor * plausible;
}

/** Issue the commander's orders into the state (the continuous-clock sheet uses this). */
/**
 * The stand-in's logistics, before it plans (X20, found by the all-rules check): with the `ammunition` rule, every
 * squad resting at home or a depot tops its rounds back up to the issue (`rules.ammoPerUnit`), keeping a new squad's
 * cost in hand when reinforcements are on. Without it the stand-in only ever ran down. State in, state out.
 */
export function standInLogistics(state: SectorState, faction: FactionId): SectorState {
  if (!state.rules.ammunition) return state;
  let s = state;
  for (const q of state.squads) {
    if (q.faction !== faction || q.at === null || !aliveUnits(q).length || !restsHere(s, faction, q.at)) continue;
    const short = state.rules.ammoPerUnit - (q.ammo ?? 0);
    if (short < 1) continue;
    const supply = s.factions.find(f => f.id === faction)?.supply ?? 0;
    const reserve = s.rules.reinforceDays > 0 ? s.rules.squadCost : 0;
    const afford = Math.floor((supply - reserve) / Math.max(1e-9, s.rules.ammoCost));
    const rounds = Math.min(short, afford);
    if (rounds >= 1) s = buyAmmo(s, q.id, rounds);
  }
  return s;
}

export function planFactionOrders(state: SectorState, faction: FactionId, style: CommanderStyle = 'balanced', opts: CommanderOptions = {}): SectorState {
  let s = state;
  for (const [id, order] of Object.entries(planFactionTurn(state, faction, style, opts))) s = issueOrder(s, id, order);
  return s;
}

/**
 * hold: a commander that keeps what it holds and strikes only next door. A squad that is the only one on a held
 * outpost stays as its garrison. Otherwise it may move to a system one lane from anything its faction holds, and
 * only where the enemy it knows of there is weaker than the squad: first where an enemy squad stands (the
 * counterattack — retaking its own first), then open ground nobody holds. Nothing beyond its doorstep.
 */
function holdTarget(state: SectorState, faction: FactionId, q: SectorState['squads'][number], strengthAt: (id: SystemId) => number): SystemId | null {
  const here = systemById(state, q.at!);
  const mineHere = state.squads.filter(x => x.faction === faction && x.at === q.at && aliveUnits(x).length > 0);
  if (here.owner === faction && mineHere.length === 1) return null;   // the outpost's only garrison stays
  // the home keeps its garrison and one reserve: they leave only to counterattack, and never while a known enemy marches
  // on the home or stands next door to it (a squad seen on a lane is placed at its destination in the view)
  const home = state.factions.find(f => f.id === faction)?.home;
  const atHome = q.at === home && mineHere.length <= 2;   // the garrison and one reserve; squads beyond them go out
  if (atHome && home && (strengthAt(home) > 0 || neighbours(state, home).some(n => strengthAt(n) > 0 && systemById(state, n).owner !== faction))) {
    const threat = neighbours(state, home).filter(n => strengthAt(n) > 0 && strengthAt(n) < aliveUnits(q).length);
    return strengthAt(home) > 0 || !threat.length ? null : threat[0];
  }
  const held = state.systems.filter(s => s.owner === faction).map(s => s.id);
  const door = new Set<SystemId>();
  for (const id of held) for (const n of neighbours(state, id)) if (systemById(state, n).owner !== faction) door.add(n);
  const mine = aliveUnits(q).length;
  let best: SystemId | null = null; let bestScore = -Infinity;
  for (const id of door) {
    const enemy = strengthAt(id);
    if (enemy >= mine) continue;
    if (atHome && enemy === 0) continue;   // the home's reserve does not go for open ground
    const path = pathBetween(state, q.at!, id);
    if (!path) continue;
    const sys = systemById(state, id);
    const wasOurs = state.log.some(e => e.kind === 'captured' && e.systemId === id && e.faction === faction);
    const score = (enemy > 0 ? 10 : 0) + (wasOurs ? 5 : 0) + (sys.owner === null ? 2 : 0) - path.length;
    if (score > bestScore) { bestScore = score; best = id; }
  }
  return best;
}

/** allIn: the nearest system an enemy holds (their home first), whatever sits there. */
function pickEnemyHeld(state: SectorState, faction: FactionId, from: SystemId): SystemId | null {
  let best: SystemId | null = null; let bd = Infinity;
  const enemyHomes = state.factions.filter(f => f.id !== faction).map(f => f.home);
  for (const sys of state.systems) {
    if (sys.id === from || sys.owner === null || sys.owner === faction) continue;
    const path = pathBetween(state, from, sys.id);
    if (!path) continue;
    const d = path.length + (enemyHomes.includes(sys.id) ? -0.5 : 0);
    if (d < bd) { bd = d; best = sys.id; }
  }
  return best;
}

function pickTarget(state: SectorState, faction: FactionId, from: SystemId, enemyHomes: SystemId[], strengthAt: (id: SystemId) => number): SystemId | null {
  const adjacentHome = neighbours(state, from).find(n => enemyHomes.includes(n) && systemById(state, n).owner !== faction);
  if (adjacentHome) return adjacentHome;
  // Nearest system (by path length) not held by us, unless a bigger enemy force than our whole faction sits there.
  // X26 (rules.valueSystems): the prize counts — a system's worth (intact planets, what is built, a home) buys it
  // closeness, so a wrecked near system can lose to an intact one a hop further
  let best: SystemId | null = null; let bd = Infinity;
  const myStrength = state.squads.filter(q => q.faction === faction).reduce((a, q) => a + aliveUnits(q).length, 0);
  const value = state.rules.valueSystems;
  for (const sys of state.systems) {
    if (sys.owner === faction || sys.id === from) continue;
    const path = pathBetween(state, from, sys.id);
    if (!path) continue;
    const enemy = strengthAt(sys.id);
    if (enemy > myStrength) continue;
    const worth = value ? systemWorth(sys, state.rules, enemyHomes.includes(sys.id)).score : 0;
    // X1 (rules.signatures): the traces the faction knows of — fresh enemy fighting there is danger, fresh enemy traffic a lane worth cutting
    const hops = state.rules.signatures ? traceHops(state.knowledge[faction]?.systems[sys.id]?.traces, faction, state.day) : 0;
    const d = path.length + (enemyHomes.includes(sys.id) ? -0.5 : 0) - (value ? worth * VALUE_HOPS : 0) + hops;
    if (d < bd) { bd = d; best = sys.id; }
  }
  return best;
}
